import React from 'react'
import { Form, Switch, Typography } from 'antd'
import styled from 'styled-components'

export type InputToggleProps = {
  label?: string
  helperText?: string
  required?: boolean
  type?: string
  onChange?: (checked: boolean) => void
  disabled?: boolean
  /** Label shown when toggle is ON. Defaults to "Yes" */
  activeLabel?: string
  /** Label shown when toggle is OFF. Defaults to "No" */
  inactiveLabel?: string
  checked?: boolean
}

function InputToggle({
  label,
  helperText,
  required,
  type,
  onChange,
  disabled,
  activeLabel = 'Yes',
  inactiveLabel = 'No',
  checked,
}: InputToggleProps) {
  const labelNode = helperText ? (
    <>
      {label}
      <HelperText>{helperText}</HelperText>
    </>
  ) : label

  const isControlled = checked !== undefined

  return (
    <StyledFormItem
      label={labelNode}
      {...(!isControlled ? { name: type, valuePropName: 'checked' } : {})}
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
      required={required}
      rules={[{ required, message: `${label} is required` }]}
      $hasHelper={!!helperText}
    >
      <ToggleContent
        onChange={onChange}
        disabled={disabled}
        activeLabel={activeLabel}
        inactiveLabel={inactiveLabel}
        checked={checked}
      />
    </StyledFormItem>
  )
}

type ToggleContentProps = {
  value?: boolean
  onChange?: (checked: boolean) => void
  disabled?: boolean
  activeLabel?: string
  inactiveLabel?: string
  checked?: boolean
}

const ToggleContent = ({
  value,
  onChange,
  disabled,
  activeLabel,
  inactiveLabel,
  checked,
}: ToggleContentProps) => {
  const isChecked = checked !== undefined ? checked : !!value

  return (
    <SwitchRow>
      <Switch
        checked={isChecked}
        onChange={(val) => onChange?.(val)}
        disabled={disabled}
      />
      {(activeLabel || inactiveLabel) && (
        <SwitchLabel>{isChecked ? activeLabel : inactiveLabel}</SwitchLabel>
      )}
    </SwitchRow>
  )
}

const HelperText = styled(Typography.Text)`
  width: 100%;
  font-weight: 400 !important;
  font-size: 13px;
  color: ${({ theme }) => theme.color.neutral.color4};
  line-height: 1.4;
  margin-bottom: -20px;
`

const StyledFormItem = styled(Form.Item)<{ $hasHelper?: boolean }>`
  .ant-form-item-control {
    margin-bottom: 8px !important;
  }

  .ant-form-item-label {
    padding-bottom: 0px !important;
    margin-bottom: -2px !important;
  }

  ${({ $hasHelper }) =>
    $hasHelper &&
    `
    .ant-form-item-label {
      overflow: visible;
      white-space: normal;
      height: auto;
      margin-bottom: 0 !important;
    }

    .ant-form-item-label > label {
      height: auto;
      flex-wrap: wrap;
    }
  `}
`

const SwitchRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;

  .ant-switch {
    min-width: 39px;
    width: 39px;
    height: 23px;
  }

  .ant-switch .ant-switch-handle {
    width: 21px;
    height: 21px;
    top: 1px;
    inset-inline-start: 1px;
  }

  .ant-switch .ant-switch-handle::before {
    border-radius: 50%;
  }

  .ant-switch-checked .ant-switch-handle {
    inset-inline-start: calc(100% - 22px);
  }
`

const SwitchLabel = styled.span`
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.color.neutral.color2};
`

export default InputToggle

